/**
 * localStorage cache for non-secret, per-user session values (permissions, app configs) so a newly
 * opened tab can render with them immediately while it refetches. Entries are tagged with the
 * user and a scope (env / API mode) and ignored when either doesn't match. Never store tokens here.
 * Other tabs are notified of writes through the browser's `storage` event.
 */

const SESSION_CACHE_PREFIX = "appf:session:";

type SessionCacheEnvelope<T> = {
  userId: string;
  scope: string;
  value: T;
  updatedAt: number;
};

export const SESSION_CACHE_KEYS = {
  PERMISSIONS: "permissions",
  appConfig: (configName: string, version: string) => `appConfig:${configName}:v${version}`
};

const toStorageKey = (key: string) => `${SESSION_CACHE_PREFIX}${key}`;

const parseEnvelope = <T>(raw: string | null, userId: string, scope: string): T | null => {
  if (!raw) return null;
  try {
    const envelope = JSON.parse(raw) as SessionCacheEnvelope<T>;
    if (envelope?.userId !== userId || envelope?.scope !== scope) return null;
    return envelope.value ?? null;
  } catch {
    return null;
  }
};

export const readSessionCache = <T>(key: string, userId: string, scope: string): T | null => {
  if (!userId) return null;
  try {
    return parseEnvelope<T>(localStorage.getItem(toStorageKey(key)), userId, scope);
  } catch {
    return null;
  }
};

export const writeSessionCache = <T>(key: string, userId: string, scope: string, value: T): void => {
  if (!userId) return;
  const envelope: SessionCacheEnvelope<T> = { userId, scope, value, updatedAt: Date.now() };
  try {
    localStorage.setItem(toStorageKey(key), JSON.stringify(envelope));
  } catch {
    // Storage full or unavailable — the cache is an optimization, so carry on without it.
  }
};

export const clearSessionCache = (): void => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(SESSION_CACHE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage unavailable — nothing to clear.
  }
};

/** Calls `onChange` when another tab writes a matching entry. Returns an unsubscribe function. */
export const subscribeSessionCache = <T>(key: string, userId: string, scope: string, onChange: (value: T) => void): (() => void) => {
  const storageKey = toStorageKey(key);
  const handleStorage = (event: StorageEvent) => {
    if (event.storageArea !== localStorage || event.key !== storageKey) return;
    const value = parseEnvelope<T>(event.newValue, userId, scope);
    if (value !== null) onChange(value);
  };
  window.addEventListener("storage", handleStorage);
  return () => window.removeEventListener("storage", handleStorage);
};
